import { useReducer } from "react"
import { Progress, ProgressByItem, ProgressInput, ProgressItemStage } from "./progress"
import { asComplete, recordKeys } from "../../utils/typescript"
import { produce } from "immer"
import uniqueId from "lodash.uniqueid"

export function useProgress<S extends string>(stages: readonly S[]) {
  return useReducer(
    progressReducer<S>,
    stages,
    initialProgress
  )
}

function initialProgress<S extends string>(stages: readonly S[]): Progress<S> {
  const byStage: Partial<Record<S, ProgressByItem>> = {}
  stages.forEach(stage => byStage[stage] = {})
  return {
    id: uniqueId("progress"),
    byStage: asComplete(byStage)
  } satisfies Progress<S>
}

function progressReducer<S extends string>(state: Progress<S>, input: ProgressInput<S>): Progress<S> {
  switch (input.type) {
    case "reset":
      return initialProgress(recordKeys(state.byStage))
    case "update":
      return produce(state, (draft: Progress<S>) => {
        const currentItem: ProgressItemStage | undefined = draft.byStage[input.stage][input.item]
        const currentProgress = currentItem && currentItem.status === "loading"
          ? currentItem.progress
          : undefined

        const stage = draft.byStage[input.stage] as ProgressByItem // else TS dislikes writing
        stage[input.item] = (() => {
          switch (input.status) {
            case "idle":
              return { status: "idle" }
            case "done":
              return { status: "done", progress: 1 }
            case "loading":
              return { status: "loading", progress: input.progress ?? currentProgress }
            case "error":
              return {
                status: "error",
                progress: currentProgress,
                error: input.error
              }
          }
        })()
      })
    default:
      throw new Error("Unknown progress input " + input["type"])
  }
}
